import { useGetList, useGetMany, type Identifier } from "ra-core";

import type { Contact, EmailRecipient } from "../types";

const MAX_CONTACTS = 100;

/** Addresses of a contact, with their name. */
export const contactRecipients = (contact: Contact): EmailRecipient[] =>
  (contact.email_jsonb ?? [])
    .filter((item) => !!item?.email)
    .map((item) => ({
      email: item.email.toLowerCase(),
      name: `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim(),
    }));

const unique = (recipients: EmailRecipient[]) =>
  recipients.filter(
    (recipient, index) =>
      recipients.findIndex((other) => other.email === recipient.email) ===
      index,
  );

/** Addresses of the contacts of a company, offered in the compose window. */
export const useCompanyRecipients = (companyId?: Identifier) => {
  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: MAX_CONTACTS },
      sort: { field: "last_name", order: "ASC" },
      filter: { "company_ids@cs": `{${companyId}}` },
    },
    { enabled: companyId != null },
  );
  return unique(contacts.flatMap(contactRecipients));
};

/** Addresses of some contacts, e.g. those of an opportunity. */
export const useContactsRecipients = (contactIds: Identifier[] = []) => {
  const { data: contacts = [] } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  return unique(contacts.flatMap(contactRecipients));
};
